import { Button, ModalV2 } from '@root/components'
import { useBusinessContext } from '@root/context'
import { deleteAccountingRule, IAccountingRule } from '@root/query'
import { queryKeyForAccountingRules } from '@root/query/hooks'
import React from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

interface Props {
  rule?: IAccountingRule
  isVisible: boolean
  handleOnClose: () => void
}

const AccountingRuleDeleteModal: React.FC<Props> = ({ rule, isVisible, handleOnClose }) => {
  const { slug } = useBusinessContext()
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  const deleteMutation = useMutation({
    mutationFn: (rule: IAccountingRule) => deleteAccountingRule({ slug, id: rule.id }),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeyForAccountingRules(slug) })

        toast.success(t('settings.automation.accountingRules.delete.success'))
      },
      onError: () => {
        toast.error(t('settings.automation.accountingRules.delete.error'))
      }
    }
  })

  const handleOnDelete = () => {
    try {
      deleteMutation.mutate(rule)
      handleOnClose()
    } catch (error) {
      console.error(error)
      return null
    }
  }

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={handleOnClose}
      header={t('settings.automation.accountingRules.delete.formHeader')}
      width={500}
      height={300}
      footer={
        <>
          <Button onClick={() => handleOnClose()}>{t('general.cancel')}</Button>
          <Button intent="danger" onClick={() => handleOnDelete()}>
            {t('general.delete')}
          </Button>
        </>
      }
    >
      <StyledContainer>
        <StyledText>{t('settings.automation.accountingRules.delete.formDescription')}</StyledText>
      </StyledContainer>
    </ModalV2>
  )
}

export default AccountingRuleDeleteModal

const StyledText = styled.p`
  margin: 0;
`
const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
`
